import { z } from "zod";
import { MoneySchema } from "../common/money.js";
import { pageOf, PageQuerySchema } from "../common/pagination.js";
import { DateOnlySchema, IdSchema, IsoDateTimeSchema } from "../common/primitives.js";
import { DisciplineSchema, GovernorateSchema } from "../common/tunisia.js";
const MISSION_TYPES = [
  "public_building",
  "collective_housing",
  "villa",
  "industrial",
  "facility",
  "subdivision_vrd",
  "expertise"
];
const MissionTypeSchema = z.enum(MISSION_TYPES);
const PHASE_TYPES = [
  "esq",
  "aps",
  "apd",
  "pro",
  "dce",
  "act",
  "det",
  "aor_provisional",
  "aor_final",
  "custom"
];
const PhaseTypeSchema = z.enum(PHASE_TYPES);
const PHASE_STATUSES = ["not_started", "in_progress", "in_review", "closed"];
const PhaseStatusSchema = z.enum(PHASE_STATUSES);
const PROJECT_STATUSES = ["draft", "active", "on_hold", "closed", "archived"];
const ProjectStatusSchema = z.enum(PROJECT_STATUSES);
const FEE_MODES = ["lump_sum", "works_percentage", "time_based"];
const FeeModeSchema = z.enum(FEE_MODES);
const PROJECT_ROLES = ["manager", "member"];
const ProjectRoleSchema = z.enum(PROJECT_ROLES);
const PROJECT_PERMISSIONS = ["can_view", "can_contribute", "can_manage"];
const ProjectPermissionSchema = z.enum(PROJECT_PERMISSIONS);
const FEE_SHARE_TOTAL_BP = 1e4;
const DataNameSchema = z.string().trim().min(1).max(120);
const FolderNameSchema = DataNameSchema.regex(/^[^/\\.][^/\\]*$/, "a folder name without / or \\");
const TemplateFolderSchema = z.lazy(
  () => z.strictObject({
    name: FolderNameSchema,
    children: z.array(TemplateFolderSchema).max(30).optional()
  })
);
const TemplatePhaseSchema = z.strictObject({
  phaseType: PhaseTypeSchema,
  /** Required for `custom` phases; the other types are named by the clients' translations. */
  name: DataNameSchema.optional(),
  /** Indicative share of the fees, basis points (the phases total 10 000). */
  feeShareBp: z.int().min(0).max(FEE_SHARE_TOTAL_BP),
  /** Indicative duration, used to suggest planned dates from the project's start. */
  durationDays: z.int().min(1).max(3650).optional()
});
const TemplateChannelSchema = z.strictObject({
  key: z.string().regex(/^[a-z][a-z0-9-]{1,39}$/),
  name: DataNameSchema,
  discipline: DisciplineSchema.optional()
});
const TemplateTaskSchema = z.strictObject({
  title: z.string().trim().min(1).max(200),
  phaseType: PhaseTypeSchema.optional(),
  discipline: DisciplineSchema.optional()
});
function folderDepth(folders) {
  return folders.reduce((max, f) => Math.max(max, 1 + folderDepth(f.children ?? [])), 0);
}
function duplicateSiblings(folders) {
  const seen = /* @__PURE__ */ new Set();
  for (const f of folders) {
    const key = f.name.toLocaleLowerCase("fr");
    if (seen.has(key)) return f.name;
    seen.add(key);
    const inner = duplicateSiblings(f.children ?? []);
    if (inner) return inner;
  }
  return null;
}
const MissionTemplateSchema = z.strictObject({
  key: MissionTypeSchema,
  phases: z.array(TemplatePhaseSchema).min(1).max(20),
  folders: z.array(TemplateFolderSchema).max(30),
  channels: z.array(TemplateChannelSchema).max(20),
  tasks: z.array(TemplateTaskSchema).max(100)
}).superRefine((t, ctx) => {
  const total = t.phases.reduce((sum, p) => sum + p.feeShareBp, 0);
  if (total !== FEE_SHARE_TOTAL_BP)
    ctx.addIssue({
      code: "custom",
      path: ["phases"],
      message: `fee shares total ${String(total)} bp, expected ${String(FEE_SHARE_TOTAL_BP)}`
    });
  const types = /* @__PURE__ */ new Set();
  t.phases.forEach((p, i) => {
    if (p.phaseType === "custom" && !p.name)
      ctx.addIssue({
        code: "custom",
        path: ["phases", i, "name"],
        message: "custom phases need a name"
      });
    if (p.phaseType !== "custom") {
      if (types.has(p.phaseType))
        ctx.addIssue({ code: "custom", path: ["phases", i], message: "phase type repeated" });
      types.add(p.phaseType);
    }
  });
  t.tasks.forEach((task, i) => {
    if (task.phaseType && task.phaseType !== "custom" && !types.has(task.phaseType))
      ctx.addIssue({
        code: "custom",
        path: ["tasks", i, "phaseType"],
        message: "the task names a phase the template does not have"
      });
  });
  if (folderDepth(t.folders) > 4)
    ctx.addIssue({ code: "custom", path: ["folders"], message: "folders nest at most 4 levels" });
  const duplicate = duplicateSiblings(t.folders);
  if (duplicate)
    ctx.addIssue({
      code: "custom",
      path: ["folders"],
      message: `folder "${duplicate}" repeated`
    });
  const keys = t.channels.map((c) => c.key);
  if (new Set(keys).size !== keys.length)
    ctx.addIssue({ code: "custom", path: ["channels"], message: "channel keys repeated" });
});
const ProjectTemplateSchema = z.strictObject({
  key: MissionTypeSchema,
  phases: z.array(
    z.strictObject({
      phaseType: PhaseTypeSchema,
      name: z.string().nullable(),
      feeShareBp: z.int().nullable()
    })
  ),
  folders: z.array(TemplateFolderSchema),
  channelCount: z.int().min(0),
  taskCount: z.int().min(0)
});
const ProjectTemplateListSchema = z.strictObject({ items: z.array(ProjectTemplateSchema) });
const ProjectCodeSchema = z.string().trim().regex(/^[A-Za-z0-9][A-Za-z0-9._/-]{0,29}$/, "a code such as 2026-014 or LYC-KAI");
const ProjectNameSchema = z.string().trim().min(1).max(200);
const RefSchema = z.string().trim().min(1).max(100);
const ProjectMoneySchema = MoneySchema.extend({
  amountMinor: z.string().regex(/^(0|[1-9]\d*)$/, "a non-negative integer string").max(18),
  currency: z.literal("TND")
});
const projectFields = {
  name: ProjectNameSchema,
  code: ProjectCodeSchema,
  missionType: MissionTypeSchema,
  clientId: IdSchema.nullable(),
  governorate: GovernorateSchema.nullable(),
  delegation: z.string().trim().min(1).max(100).nullable(),
  latitude: z.number().min(-90).max(90).nullable(),
  longitude: z.number().min(-180).max(180).nullable(),
  surfaceM2: z.int().min(1).max(1e7).nullable(),
  worksCostEstimate: ProjectMoneySchema.nullable(),
  feeMode: FeeModeSchema.nullable(),
  feeAmount: ProjectMoneySchema.nullable(),
  contractRef: RefSchema.nullable(),
  tunepsRef: z.string().trim().min(1).max(50).nullable(),
  startsOn: DateOnlySchema.nullable(),
  endsOn: DateOnlySchema.nullable()
};
function projectFieldIssues(p) {
  const issues = [];
  if (p.startsOn && p.endsOn && p.endsOn < p.startsOn)
    issues.push({ path: "endsOn", message: "the end date is before the start date" });
  if (p.latitude == null !== (p.longitude == null))
    issues.push({ path: "longitude", message: "give both coordinates or neither" });
  return issues;
}
const CreateProjectRequestSchema = z.strictObject({
  name: projectFields.name,
  code: projectFields.code.optional(),
  missionType: projectFields.missionType,
  clientId: projectFields.clientId.optional(),
  governorate: projectFields.governorate.optional(),
  delegation: projectFields.delegation.optional(),
  latitude: projectFields.latitude.optional(),
  longitude: projectFields.longitude.optional(),
  surfaceM2: projectFields.surfaceM2.optional(),
  worksCostEstimate: projectFields.worksCostEstimate.optional(),
  feeMode: projectFields.feeMode.optional(),
  feeAmount: projectFields.feeAmount.optional(),
  contractRef: projectFields.contractRef.optional(),
  tunepsRef: projectFields.tunepsRef.optional(),
  startsOn: projectFields.startsOn.optional(),
  endsOn: projectFields.endsOn.optional(),
  /** An organisation template (PLN-BE-03): its structure replaces the mission type's. */
  orgTemplateId: IdSchema.optional()
}).superRefine((p, ctx) => {
  for (const issue of projectFieldIssues(p))
    ctx.addIssue({ code: "custom", path: [issue.path], message: issue.message });
});
const UpdateProjectRequestSchema = z.strictObject({ ...projectFields, status: ProjectStatusSchema }).partial().refine((p) => Object.keys(p).length > 0, { message: "nothing to update" });
const ProjectParamsSchema = z.strictObject({ orgId: IdSchema, projectId: IdSchema });
const ProjectSchema = z.strictObject({
  id: IdSchema,
  code: z.string(),
  name: z.string(),
  missionType: MissionTypeSchema,
  status: ProjectStatusSchema,
  client: z.strictObject({ id: IdSchema, name: z.string() }).nullable(),
  governorate: GovernorateSchema.nullable(),
  delegation: z.string().nullable(),
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
  surfaceM2: z.int().nullable(),
  worksCostEstimate: MoneySchema.nullable(),
  /** Null without `project.fees.read`. */
  fees: z.strictObject({ mode: FeeModeSchema.nullable(), amount: MoneySchema.nullable() }).nullable(),
  contractRef: z.string().nullable(),
  tunepsRef: z.string().nullable(),
  startsOn: DateOnlySchema.nullable(),
  endsOn: DateOnlySchema.nullable(),
  internalSpaceId: IdSchema.nullable(),
  phaseProgress: z.strictObject({
    closed: z.int().min(0),
    total: z.int().min(0),
    /** The first phase that is not closed, in order. */
    current: z.strictObject({ id: IdSchema, phaseType: PhaseTypeSchema, name: z.string().nullable() }).nullable()
  }),
  /** The caller's role in the project; null for admins who are not members. */
  myRole: ProjectRoleSchema.nullable(),
  archivedAt: IsoDateTimeSchema.nullable(),
  createdAt: IsoDateTimeSchema,
  version: z.int().min(1),
  _permissions: z.array(ProjectPermissionSchema)
});
const ProjectListQuerySchema = PageQuerySchema.extend({
  q: z.string().trim().min(1).max(100).optional(),
  status: ProjectStatusSchema.optional(),
  missionType: MissionTypeSchema.optional(),
  clientId: IdSchema.optional(),
  mine: z.enum(["true", "false"]).default("false")
});
const ProjectListSchema = pageOf(ProjectSchema);
const ProjectMemberSchema = z.strictObject({
  userId: IdSchema,
  displayName: z.string(),
  projectRole: ProjectRoleSchema,
  addedAt: IsoDateTimeSchema
});
const ProjectMemberListSchema = z.strictObject({ items: z.array(ProjectMemberSchema) });
const PutProjectMembersRequestSchema = z.strictObject({
  members: z.array(z.strictObject({ userId: IdSchema, projectRole: ProjectRoleSchema })).min(1).max(200)
}).superRefine((body, ctx) => {
  const ids = body.members.map((m) => m.userId);
  if (new Set(ids).size !== ids.length)
    ctx.addIssue({ code: "custom", path: ["members"], message: "a member is listed twice" });
  if (!body.members.some((m) => m.projectRole === "manager"))
    ctx.addIssue({ code: "custom", path: ["members"], message: "a project needs a manager" });
});
export {
  CreateProjectRequestSchema,
  FEE_MODES,
  FEE_SHARE_TOTAL_BP,
  FeeModeSchema,
  MISSION_TYPES,
  MissionTemplateSchema,
  MissionTypeSchema,
  PHASE_STATUSES,
  PHASE_TYPES,
  PROJECT_PERMISSIONS,
  PROJECT_ROLES,
  PROJECT_STATUSES,
  PhaseStatusSchema,
  PhaseTypeSchema,
  ProjectCodeSchema,
  ProjectListQuerySchema,
  ProjectListSchema,
  ProjectMemberListSchema,
  ProjectMemberSchema,
  ProjectParamsSchema,
  ProjectPermissionSchema,
  ProjectRoleSchema,
  ProjectSchema,
  ProjectStatusSchema,
  ProjectTemplateListSchema,
  ProjectTemplateSchema,
  PutProjectMembersRequestSchema,
  TemplateChannelSchema,
  TemplateFolderSchema,
  TemplatePhaseSchema,
  TemplateTaskSchema,
  UpdateProjectRequestSchema,
  projectFieldIssues
};
//# sourceMappingURL=index.js.map